/**
 * Customer-facing MRE order form: collects RAW/AVV details plus the vehicle's VIN and
 * build date, then creates a `modelreports` row, waits for the invoice/transaction
 * (Payment Pending), and directs the customer to Cart — or Dashboard if invoicing is still pending.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useAuth, supabase } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import CustomerLayout from './CustomerLayout';

const states = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

// AVV options are hardcoded because the AVV approval number and email are fixed.
const AVV_OPTIONS = [
  {
    approvalNumber: 'AVV-000069',
    email: '10411raw@gmail.com'
  }
];

const formatBuildDateInput = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const getBuildDateError = (value, { requireComplete = false } = {}) => {
  if (!value) return 'Build Date is required';
  if (value.length < 7) {
    return requireComplete ? 'Build Date must be in MM/YYYY format' : '';
  }
  const match = value.match(/^(\d{2})\/(\d{4})$/);
  if (!match) return 'Build Date must be in MM/YYYY format';
  const month = Number(match[1]);
  if (month < 1 || month > 12) return 'Month must be between 01 and 12';
  return '';
};

const fieldClassName = (error, readOnly = false, disabled = false) =>
  `mt-1 block w-full rounded-2xl border px-4 py-3 text-sm sm:text-base shadow-sm outline-none transition-colors duration-200 focus:border-[#ff0000] focus:ring focus:ring-[#ff0000] focus:ring-opacity-50 ${error ? 'border-[#ff0000]' : 'border-gray-300'
  } ${readOnly || disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`;

const inputClassName = fieldClassName;

/** Fire-and-forget: SMS/email Sami when this is the customer's first order. Never blocks submit. */
const notifyFirstOrderIfNeeded = async ({
  accessToken,
  userId,
  name,
  phone,
  carMake,
  carModel,
}) => {
  try {
    await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/notify-first-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({
        user_id: userId,
        customer_name: name,
        customer_phone: phone,
        car_make: carMake,
        car_model: carModel,
      }),
    });
  } catch (error) {
    console.error('First-order notify failed (non-blocking):', error);
  }
};

/** How long we keep the Processing overlay while the invoice/transaction is created. */
const INVOICE_READY_TIMEOUT_MS = 15_000;
const INVOICE_POLL_MS = 1_000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithTimeout = async (url, options, timeoutMs) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

/** Poll until the submission has a Payment Pending transaction (visible in Cart). */
const waitUntilPaymentPending = async (submissionId, userId, timeoutMs) => {
  const deadline = Date.now() + Math.max(0, timeoutMs);

  while (true) {
    const { data, error } = await supabase
      .from('admin_submissions_view')
      .select('payment_status')
      .eq('id', submissionId)
      .eq('user_id', userId)
      .maybeSingle();

    if (!error && data?.payment_status === 'Payment Pending') {
      return true;
    }

    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      return false;
    }

    await sleep(Math.min(INVOICE_POLL_MS, remaining));
  }
};

/**
 * Create the invoice/transaction after order insert, then wait until Cart can show it.
 * Returns true when payment_status is Payment Pending within the timeout; otherwise false
 * (order is still placed — Dashboard handles Processing / recovery).
 */
const prepareInvoiceForCart = async ({
  accessToken,
  submissionId,
  userId,
  vinNo,
  selectedModelReportId,
}) => {
  const startedAt = Date.now();
  let invoiceQueuedForRetry = false;

  try {
    const response = await fetchWithTimeout(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-common-price`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          submissionId,
          user_id: userId,
          vinNo,
          selectedModelReportId,
        }),
      },
      INVOICE_READY_TIMEOUT_MS
    );

    if (response?.ok) {
      const body = await response.json().catch(() => ({}));
      // check-common-price returns pending:true when create-invoice failed and recovery was queued.
      invoiceQueuedForRetry = Boolean(body?.pending);
    }
  } catch (error) {
    console.error('Invoice trigger failed or timed out:', error);
  }

  // Brief grace poll if recovery was queued; otherwise use the remaining overlay budget.
  const remainingMs = INVOICE_READY_TIMEOUT_MS - (Date.now() - startedAt);
  const pollMs = invoiceQueuedForRetry
    ? Math.min(2_000, Math.max(0, remainingMs))
    : Math.max(0, remainingMs);

  return waitUntilPaymentPending(submissionId, userId, pollMs);
};

const SelectInput = ({ label, error, children, ...props }) => (
  <div className="text-left">
    <label htmlFor={props.id} className="block text-sm font-medium text-[#333333] mb-1">
      {label}
      <span className="text-[#ff0000] ml-1">*</span>
    </label>
    <div className="relative">
      <select
        {...props}
        className={`${fieldClassName(error)} appearance-none pr-10`}
      >
        {children}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
    </div>
    {error && <p className="mt-1 text-sm text-[#ff0000]">{error}</p>}
  </div>
);

const FormInput = ({ label, error, readOnly, required = true, helperText, disabled, ...props }) => (
  <div className="text-left">
    <label htmlFor={props.id} className="block text-sm font-medium text-[#333333] mb-1">
      {label}
      {required && <span className="text-[#ff0000] ml-1">*</span>}
    </label>
    <input
      {...props}
      readOnly={readOnly}
      disabled={disabled}
      className={inputClassName(error, readOnly, disabled)}
    />
    {helperText && <p className="mt-1 text-sm text-gray-500">{helperText}</p>}
    {error && <p className="mt-1 text-sm text-[#ff0000]">{error}</p>}
  </div>
);

// A combobox input that allows users to select from a list of options or type their own value. The dropdown opens when the input is focused or clicked, and closes when it loses focus. Selecting an option updates the input value and triggers the onChange callback.  
const ComboboxInput = ({ label, value, onChange, options, name, type = 'text', placeholder, error }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="text-left">
      <label htmlFor={name} className="block text-sm font-medium text-[#333333] mb-1">
        {label}
        <span className="text-[#ff0000] ml-1">*</span>
      </label>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={onChange}
          name={name}
          id={name}
          onClick={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          className={`${fieldClassName(error)} pr-10`}
          placeholder={placeholder}
          autoComplete="off"
        />
        <div
          className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-2xl shadow-lg overflow-hidden">
            {options.map((option, index) => (
              <div
                key={index}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                onClick={() => {
                  onChange({
                    target: {
                      name: name,
                      value: option.value
                    }
                  });
                  if (option.onSelect) {
                    option.onSelect();
                  }
                  setIsOpen(false);
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-[#ff0000]">{error}</p>}
    </div>
  );
};

// Terms and Conditions Modal Component
const TermsAndConditionsModal = ({ isOpen, onClose, onAccept }) => {
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsChecked(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-3 py-3 sm:px-4 sm:py-6 backdrop-blur-[3px]">
      <div className="flex w-full max-w-3xl max-h-[96vh] sm:max-h-[90vh] flex-col overflow-hidden rounded-2xl sm:rounded-[2rem] border border-slate-200 bg-white shadow-[0_18px_40px_-24px_rgba(15,23,42,0.35)]">
        <div className="shrink-0 border-b border-slate-200 px-4 py-2.5 sm:px-8 sm:py-5">
          <h2 className="text-sm font-medium uppercase tracking-[0.04em] text-slate-900">
            Deed of License
          </h2>
          <p className="mt-0.5 text-sm text-slate-600 sm:mt-2">
            Please review the license terms below before continuing with your submission.
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-8 sm:py-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 sm:rounded-2xl sm:p-6">
            <div className="space-y-4 text-sm leading-6 text-slate-700">
              <p>This Deed is made between Model Report Author, ADR Model Reports Pty Ltd (ACN-634 641 811) or Shui Mir Pty Ltd (ACN- 663 560 110), hereinafter referred to as &quot;the Owner&quot; and any Registered Automotive Workshop or other authorised purchaser of a Model Report, hereinafter referred to as &quot;the Customer&quot;.</p>
              <p>By purchasing, accessing, downloading or using a Model Report, the Customer acknowledges that they have read, understood and agree to be bound by the terms of this Deed.</p>
              <p>Each Model Report is licensed for one vehicle only and is associated with a specific VIN and Unique ID issued by the Owner. The Customer's right to use the Model Report is strictly limited to that vehicle and VIN and is subject to the terms of this Deed.</p>

              <div>
                <div className="mt-6 space-y-6 border-t border-slate-200 pt-6">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">TERMS OF LICENCE</h3>
                  <div>
                    <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">1. Ownership</h4>
                    <p>The Customer acknowledges that the Model Report and all information, content, intellectual property and materials contained within it remain the property of the Owner. Payment for a Model Report does not transfer ownership or intellectual-property rights to the Customer.</p>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">2. Limited Licence</h4>
                    <p>Subject to full payment, the Owner grants the Customer a limited, non-exclusive, non-transferable and non-sublicensable licence to use the Model Report solely for the authorised purposes set out in this Deed.</p>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">3. One Vehicle Only</h4>
                    <p>Each Model Report is licensed for one vehicle only and is linked to the specific VIN and Unique ID issued by the Owner.</p>
                    <p className="mt-3">The Model Report and Unique ID must not be used, transferred or reused for another vehicle, even if the vehicle has the same make, model, variant or specifications.</p>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">4. Authorised Use</h4>
                    <p>The Customer may use the Model Report only for demonstrating vehicle or component compliance to Vehicle Safety Standards staff or authorised auditors, meeting applicable audit requirements, assisting employees with compliance, preparing Vehicle Inspection Certificates and Consumer Information Notices, and complying with applicable requirements under the Road Vehicle Standards Act (RVSA) and related legislation.</p>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">5. No Copying or Sharing</h4>
                    <p>The Customer must not copy, reproduce, modify, distribute, publish, sell, resell, sublicense or provide the Model Report or any part of it to another person or entity without the Owner&apos;s prior written consent.</p>
                    <p className="mt-3">The Customer must take reasonable steps to prevent unauthorised access, copying or use of the Model Report.</p>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">6. Unique ID and VIN</h4>
                    <p>The Customer is responsible for ensuring that the Model Report and Unique ID are used only with the vehicle and VIN for which they were issued.</p>
                    <p className="mt-3">The Customer must not alter, duplicate, transfer, reuse or otherwise misuse the Unique ID or use it in connection with another vehicle.</p>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">7. Regulatory Changes</h4>
                    <p>The Model Report is supplied based on the requirements applicable at the time of preparation.</p>
                    <p className="mt-3">The Owner is not responsible for future changes to legislation, regulations, standards, departmental requirements or their enforcement, and is not obliged to provide updates unless separately agreed in writing.</p>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">8. Breach</h4>
                    <p>Any unauthorised copying, sharing, disclosure, resale, transfer, reuse or other unauthorised use of the Model Report or Unique ID constitutes a breach of this Deed.</p>
                    <p className="mt-3">In the event of a breach, the Owner may suspend or terminate the licence, require the Customer to immediately cease using and return or permanently delete the Model Report and any unauthorised copies, and pursue any other rights or remedies available under applicable law.</p>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">9. Termination / De-registration</h4>
                    <p>If the Customer&apos;s licence is terminated or the Registered Automotive Workshop is dissolved or de-registered, the Customer must immediately cease using the Model Report and, within 14 days, return or permanently delete the Model Report and any unauthorised copies, subject to any legal requirement to retain a copy.</p>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">10. Compliance and Responsibility</h4>
                    <p>The Customer is responsible for using the correct Model Report, VIN and Unique ID for the relevant vehicle and for complying with all applicable laws, regulations and requirements.</p>
                    <p className="mt-3">The Model Report does not constitute a guarantee that a regulatory authority will accept the vehicle or Model Report in every circumstance.</p>
                    <p className="mt-3">Nothing in this Deed excludes, restricts or modifies any rights or obligations that cannot lawfully be excluded or modified under applicable Australian law.</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-6">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.04em] text-slate-900">CUSTOMER ACKNOWLEDGEMENT</h3>
                <p>By purchasing, accessing, downloading or using a Model Report, the Customer acknowledges and agrees that:</p>
                <ul className="mt-3 list-disc space-y-2 pl-5">
                  <li>the Model Report remains the property of the Owner;</li>
                  <li>the Customer receives a limited licence to use the Model Report and does not acquire ownership or intellectual-property rights in it;</li>
                  <li>the Model Report is licensed for one vehicle only;</li>
                  <li>the Model Report may only be used with the VIN and Unique ID allocated to that vehicle;</li>
                  <li>the Model Report and Unique ID must not be copied, transferred, reused, resold or provided to an unauthorised person;</li>
                  <li>the Customer is responsible for preventing unauthorised access or use of the Model Report; and</li>
                  <li>purchasing, accessing, downloading or using the Model Report constitutes acceptance of the terms of this Deed.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 px-4 py-2.5 sm:px-8 sm:py-5">
          <label
            htmlFor="terms-checkbox"
            className="mb-2 flex cursor-pointer items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 transition hover:bg-slate-100 sm:mb-5 sm:gap-3 sm:rounded-2xl sm:px-4 sm:py-3"
          >
            <input
              type="checkbox"
              id="terms-checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
            />
            <span className="text-sm font-medium text-[#333333]">
              I have read and agree to the terms and conditions
            </span>
          </label>

          <div className="flex flex-row gap-2 sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:flex-none sm:rounded-2xl sm:px-6 sm:py-3"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onAccept}
              disabled={!isChecked}
              className={`flex-1 rounded-xl border border-transparent px-3 py-2 text-sm font-medium text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 sm:flex-none sm:rounded-2xl sm:px-6 sm:py-3 ${isChecked
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'cursor-not-allowed bg-blue-300 opacity-70'
                }`}
            >
              Accept &amp; Continue
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const MrAvvForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scenario, setScenario] = useState('Model Report');
  const [mreStartDate, setMreStartDate] = useState('');
  const [mreEndDate, setMreEndDate] = useState('');
  // The formData state holds all the input values for the form, including user details, vehicle details, and AVV information. It is initialized with default values, and will be updated as the user fills out the form.
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    carMake: '',
    carModel: '',
    rawApprovalNumber: '',
    mreUsed: '',
    chassiscode: '',
    vinNo: '',
    buildDate: '',
    state: '',
    uniqueId: '',
    status: 'Processing',
    paymentStatus: 'Not Sent',
    price: 440,
    avvApprovalNumber: '',
    avvEmail: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  /** True when invoice is ready for Cart; false → send user to Dashboard instead. */
  const [cartInvoiceReady, setCartInvoiceReady] = useState(true);
  const [submissionDetails, setSubmissionDetails] = useState(null);
  const [mreDetails, setMreDetails] = useState(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [vinCheckLoading, setVinCheckLoading] = useState(false);

  // Prefill the workshop's identity from their saved profile so repeat orders only need
  // the vehicle details.
  useEffect(() => {
    const fetchPreviousSubmission = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('users')
          .select('name, email, phone, raw')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching user details:', error);
          return;
        }

        // Set form data with user details from both auth context and database
        setFormData(prevData => ({
          ...prevData,
          name: data?.name || '',
          email: user?.email || data?.email || '',  // Prefer auth context email
          phone: '+' + user?.phone || '+' + data?.phone || '',  // Prefer auth context phone
          rawApprovalNumber: data?.raw?.substring(4) || ''
        }));

        // Only set as existing user if we have the required fields
        if (data?.name && (user?.email || data?.email) && (user?.phone || data?.phone) && data?.raw) {
          setIsExistingUser(true);
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    };

    fetchPreviousSubmission();
  }, [user]);

  // An MRE can arrive two ways: posted back from the model report database popup, or as
  // router state when the customer navigated here from that page in the same tab.
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'MRE_SELECTED') {
        setMreStartDate(event.data.mreDetails.startDate);
        setMreEndDate(event.data.mreDetails.endDate === 'Ongoing' ? getCurrentMonthYear() : event.data.mreDetails.endDate);

        setFormData(prev => ({
          ...prev,
          mreUsed: event.data.mreId.toString(),
          carMake: event.data.mreDetails.make,
          carModel: event.data.mreDetails.model,
          chassiscode: event.data.mreDetails.chassisCode,
        }));
        setMreDetails(event.data.mreDetails);

        // Clear errors for the fields that are now filled
        setErrors(prev => ({
          ...prev,
          mreUsed: "",
          carMake: "",
          carModel: "",
          chassiscode: ""
        }));
      }
    };

    // Handle state passed from ModelReportDatabase
    if (location.state?.mreDetails) {
      setMreStartDate(location.state.mreDetails.startDate);
      setMreEndDate(location.state.mreDetails.endDate === 'Ongoing' ? getCurrentMonthYear() : location.state.mreDetails.endDate);

      setFormData(prev => ({
        ...prev,
        mreUsed: location.state.mreId.toString(),
        carMake: location.state.mreDetails.make,
        carModel: location.state.mreDetails.model,
        chassiscode: location.state.mreDetails.chassisCode,
      }));
      setMreDetails(location.state.mreDetails);

      // Clear errors for the fields that are now filled
      setErrors(prev => ({
        ...prev,
        mreUsed: "",
        carMake: "",
        carModel: "",
        chassiscode: ""
      }));
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [location.state]);

  // Cleanup VIN check timer on unmount
  useEffect(() => {
    return () => {
      if (vinCheckTimerRef.current) {
        clearTimeout(vinCheckTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Only clear error if the field has a value
    if (value.trim()) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }

    if (name === 'phone') {
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 10) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
      }
      return;
    }

    if (name === 'rawApprovalNumber') {
      // Only allow numbers and limit to 6 digits
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 6) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
      }
      return;
    }

    if (name === 'buildDate') {
      const formatted = formatBuildDateInput(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
      if (formatted) {
        setErrors(prev => ({ ...prev, buildDate: getBuildDateError(formatted) }));
      } else {
        setErrors(prev => ({ ...prev, buildDate: '' }));
      }
      return;
    }

    // Handle VIN Number validation
    if (name === 'vinNo') {
      if (value.length > 17) {
        return; // Don't update if already at max length
      }

      if (value.length > 17) {
        setErrors(prev => ({
          ...prev,
          vinNo: "VIN cannot be more than 17 characters"
        }));
      } else {
        // Clear length error and trigger uniqueness check
        setErrors(prev => ({
          ...prev,
          vinNo: ""
        }));

        // Wait for at least 4 characters before querying so early keystrokes don't match
        // a large share of existing VINs.
        if (value.length >= 4) {
          debouncedVinCheck(value);
        }
      }
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    // Handle State validation
    if (name === 'state') {
      if (!value) {
        setErrors(prev => ({
          ...prev,
          state: "State is required"
        }));
      }
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    // Handle AVV Approval Number validation
    if (name === 'avvApprovalNumber') {
      if (!value.trim()) {
        setErrors(prev => ({
          ...prev,
          avvApprovalNumber: "AVV Approval Number is required"
        }));
      }
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    // Handle AVV Email validation
    if (name === 'avvEmail') {
      if (!value.trim()) {
        setErrors(prev => ({
          ...prev,
          avvEmail: "AVV Email is required"
        }));
      }
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // MM/YYYY for the current month, matching the build/approval date format used elsewhere.
  const getCurrentMonthYear = () => {
    const date = new Date();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Ensure two digits
    const year = date.getFullYear();
    return `${month}/${year}`;
  };

  // A Model Report is licensed to one vehicle, so a VIN may only ever be ordered once.
  // Uses edge function (service role) so first-time customers still see VINs used by others
  // — client RLS only returns the current user's modelreports rows.
  // Matching stays case-insensitive (same as before): 6ZZH... equals 6zzh...
  const checkVinUniqueness = async (vinNumber) => {
    if (!vinNumber || vinNumber.length < 4) return true; // Skip check for very short VINs

    try {
      setVinCheckLoading(true);

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/check-vin-uniqueness`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ vin_number: String(vinNumber).trim() }),
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.error('Error checking VIN uniqueness:', result);
        return true; // Same as before: allow submission if check fails
      }

      if (result.isUnique === false) {
        setErrors((prev) => ({
          ...prev,
          vinNo:
            'This VIN is already registered to another customer and cannot be reused. Each VIN can only be used once. If you believe this is incorrect, please contact us on +61 432 333 404 for assistance.',
        }));
        return false;
      }

      setErrors((prev) => ({
        ...prev,
        vinNo: '',
      }));
      return true;
    } catch (error) {
      console.error('Error during VIN check:', error);
      return true; // Same as before: allow submission if check fails
    } finally {
      setVinCheckLoading(false);
    }
  };

  // Ref to store the debounce timer
  const vinCheckTimerRef = useRef(null);

  // Debounced VIN check to avoid too many API calls
  const debouncedVinCheck = useCallback((vinNumber) => {
    // Clear previous timer
    if (vinCheckTimerRef.current) {
      clearTimeout(vinCheckTimerRef.current);
    }

    // Set new timer
    vinCheckTimerRef.current = setTimeout(() => {
      checkVinUniqueness(vinNumber);
    }, 1000); // Wait 1 second after user stops typing
  }, []);

  /**
   * Validates the form and creates the order. When `isMoveToCart` is true this stops after
   * validation and opens the deed of licence, which then submits via `handleTermsAccepted`.
   */
  const handleSubmit = async (e, isMoveToCart = false) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Only set errors for empty fields
    const newErrors = {};

    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.phone) newErrors.phone = "Phone number is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.rawApprovalNumber) newErrors.rawApprovalNumber = "RAW Approval Number is required";
    if (!formData.mreUsed) newErrors.mreUsed = "MRE selection is required";
    if (!formData.carMake) newErrors.carMake = "Make is required";
    if (!formData.carModel) newErrors.carModel = "Model is required";
    if (!formData.chassiscode) newErrors.chassiscode = "Chassis Code is required";
    const buildDateError = getBuildDateError(formData.buildDate, { requireComplete: true });
    if (buildDateError) newErrors.buildDate = buildDateError;
    if (!formData.vinNo) newErrors.vinNo = "VIN is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.avvApprovalNumber) newErrors.avvApprovalNumber = "AVV Approval Number is required";
    if (!formData.avvEmail) newErrors.avvEmail = "AVV Email is required";

    setErrors(newErrors);

    // If there are any errors, stop submission
    if (Object.keys(newErrors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    // Check VIN uniqueness before proceeding with submission
    const isVinUnique = await checkVinUniqueness(formData.vinNo);
    if (!isVinUnique) {
      setIsSubmitting(false);
      return;
    }

    if (isMoveToCart) {
      setShowTermsModal(true);
      setIsSubmitting(false);
      return;
    }

    try {
      const nullIfEmpty = value => value === '' ? null : value;
      const { data: authData } = await supabase.auth.getUser();
      const authUserId = authData?.user?.id || user?.id;

      if (!authUserId) {
        throw new Error('User session not found. Please login again.');
      }

      // First, update the user's table with RAW number and name if not existing
      const { error: userUpdateError } = await supabase
        .from('users')
        .update({
          raw: 'RAW-' + formData.rawApprovalNumber,
          name: formData.name
        })
        .eq('id', authUserId);

      if (userUpdateError) {
        console.error('Error updating user:', userUpdateError);
      }

      // Now insert into modelreports with the foreign key
      const { data: modelReportData, error: modelReportError } = await supabase
        .from('modelreports')
        .insert([{
          user_id: authUserId,
          mr_id: formData.mreUsed,
          build_date: nullIfEmpty(formData.buildDate),
          vin_number: nullIfEmpty(formData.vinNo.trim().toUpperCase()),
          state: nullIfEmpty(formData.state),
          avv_approval_number: nullIfEmpty(formData.avvApprovalNumber),
          avv_approval_email: nullIfEmpty(formData.avvEmail),
        }])
        .select();

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (modelReportError) {
        throw modelReportError;
      }

      if (modelReportData) {
        setIsProcessingOrder(true);

        // Hold the Processing overlay until Cart can show the order (or timeout).
        const invoiceReady = await prepareInvoiceForCart({
          accessToken,
          submissionId: modelReportData[0].id,
          userId: authUserId,
          vinNo: formData.vinNo.trim().toUpperCase(),
          selectedModelReportId: formData.mreUsed,
        });

        // First-order notify stays non-blocking — it must not delay the success modal.
        void notifyFirstOrderIfNeeded({
          accessToken,
          userId: authUserId,
          name: formData.name,
          phone: formData.phone,
          carMake: formData.carMake,
          carModel: formData.carModel,
        });

        setSubmissionDetails({
          id: modelReportData[0].id,
          scenario
        });
        setCartInvoiceReady(invoiceReady);
        setShowCartModal(true);
        window.dispatchEvent(new Event('customer-cart-updated'));
      }
    } catch (error) {
      console.error('Detailed error:', error);
      alert(`Error submitting form: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      setIsProcessingOrder(false);
    }
  };

  const handleTermsAccepted = async () => {
    setShowTermsModal(false);
    setIsProcessingOrder(true);

    // Check VIN uniqueness before proceeding with submission
    const isVinUnique = await checkVinUniqueness(formData.vinNo);
    if (!isVinUnique) {
      setIsProcessingOrder(false);
      setShowTermsModal(true);
      return;
    }

    try {
      const nullIfEmpty = value => value === '' ? null : value;
      const { data: authData } = await supabase.auth.getUser();
      const authUserId = authData?.user?.id || user?.id;

      if (!authUserId) {
        throw new Error('User session not found. Please login again.');
      }

      // First, update the user's table with RAW number and name if not existing
      const { error: userUpdateError } = await supabase
        .from('users')
        .update({
          raw: 'RAW-' + formData.rawApprovalNumber,
          name: formData.name
        })
        .eq('id', authUserId);

      if (userUpdateError) {
        console.error('Error updating user:', userUpdateError);
      }

      // Now insert into modelreports with the foreign key
      const { data: modelReportData, error: modelReportError } = await supabase
        .from('modelreports')
        .insert([{
          user_id: authUserId,
          mr_id: formData.mreUsed,
          build_date: nullIfEmpty(formData.buildDate),
          vin_number: nullIfEmpty(formData.vinNo.trim().toUpperCase()),
          state: nullIfEmpty(formData.state),
          avv_approval_number: nullIfEmpty(formData.avvApprovalNumber),
          avv_approval_email: nullIfEmpty(formData.avvEmail),
        }])
        .select();

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (modelReportError) {
        throw modelReportError;
      }

      if (modelReportData) {
        // Hold the Processing overlay until Cart can show the order (or timeout).
        const invoiceReady = await prepareInvoiceForCart({
          accessToken,
          submissionId: modelReportData[0].id,
          userId: authUserId,
          vinNo: formData.vinNo.trim().toUpperCase(),
          selectedModelReportId: formData.mreUsed,
        });

        // First-order notify stays non-blocking — it must not delay the success modal.
        void notifyFirstOrderIfNeeded({
          accessToken,
          userId: authUserId,
          name: formData.name,
          phone: formData.phone,
          carMake: formData.carMake,
          carModel: formData.carModel,
        });

        setSubmissionDetails({
          id: modelReportData[0].id,
          scenario
        });
        setCartInvoiceReady(invoiceReady);
        setShowCartModal(true);
        window.dispatchEvent(new Event('customer-cart-updated'));
      }
    } catch (error) {
      console.error('Detailed error:', error);
      alert(`Error submitting form: ${error.message}`);
      setShowTermsModal(true);
    } finally {
      setIsProcessingOrder(false);
    }
  };


  const openModelReportDatabasePopup = () => {
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      '/modelreportdatabase',
      'ModelReportDatabaseWindow',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=no,location=no,toolbar=no`
    );
  };

  return (
    <CustomerLayout
      activeItemId="new-order"
      title="New Order"
      subtitle="Submit your details to get started with your MRE document request."
      contentClassName="mx-auto max-w-6xl"
    >
      <div className="w-full pb-12">
        <div className="mx-auto max-w-6xl rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:p-8">
          <div className="mb-8 flex flex-col gap-3 border-b border-slate-200 pb-6">
            <h2 className="text-md font-medium text-slate-900 uppercase tracking-[0.04em]">
              Model Report (MRE) Submission Form
            </h2>
            <p className="max-w-3xl text-sm text-slate-600 sm:text-base">
              Complete the information below to submit your MRE document request. All fields are required.
            </p>
          </div>

          <form className="space-y-8" onSubmit={(e) => handleSubmit(e, false)}>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <FormInput
                label="RAW Approval Number"
                id="rawApprovalNumber"
                name="rawApprovalNumber"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={formData.rawApprovalNumber}
                onChange={handleChange}
                placeholder="Enter 6 digit RAW Approval Number"
                error={errors.rawApprovalNumber}
                readOnly={isExistingUser}
                helperText="Example: 000001"
              />
              <FormInput
                label="Name"
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter Name"
                error={errors.name}
                readOnly={isExistingUser}
              />
              <FormInput
                label="Email"
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter Email"
                error={errors.email}
                readOnly={true}
              />
              <FormInput
                label="Phone"
                id="phone"
                name="phone"
                type="tel"
                inputMode="numeric"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter Phone number"
                error={errors.phone}
                readOnly={true}
              />

              {/* MRE picker: the field only displays the selection, which the popup supplies,
                  along with the make, model and chassis code below */}
              <div className="sm:col-span-2 text-left">
                <label htmlFor="mreUsed" className="block text-sm font-medium text-[#333333] mb-1">
                  MRE
                  <span className="text-[#ff0000] ml-1">*</span>
                </label>
                <div className="flex gap-3">
                  <input
                    id="mreUsed"
                    name="mreUsed"
                    type="text"
                    value={
                      mreDetails
                        ? `${mreDetails.mrApproval} - ${mreDetails.make} ${mreDetails.model} (${mreDetails.chassisCode})`
                        : 'No MRE Selected'
                    }
                    onChange={handleChange}
                    readOnly
                    className={`${fieldClassName(errors.mreUsed, true)} min-w-0 flex-1`}
                  />
                  <button
                    type="button"
                    onClick={openModelReportDatabasePopup}
                    className="mt-1 shrink-0 rounded-2xl border border-transparent bg-blue-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                  >
                    Select
                  </button>
                </div>
                {errors.mreUsed && <p className="mt-1 text-sm text-[#ff0000]">{errors.mreUsed}</p>}
              </div>

              <FormInput
                label="Make"
                id="carMake"
                name="carMake"
                type="text"
                value={formData.carMake || 'Not Selected'}
                onChange={handleChange}
                readOnly={true}
                error={errors.carMake}
              />
              <FormInput
                label="Model"
                id="carModel"
                name="carModel"
                type="text"
                value={formData.carModel || 'Not Selected'}
                onChange={handleChange}
                readOnly={true}
                error={errors.carModel}
              />

              {scenario && (
                <>
                  <FormInput
                    label="Build Date"
                    id="buildDate"
                    name="buildDate"
                    type="text"
                    inputMode="numeric"
                    maxLength={7}
                    value={formData.buildDate || ''}
                    onChange={handleChange}
                    error={errors.buildDate}
                    placeholder="MM/YYYY"
                  />
                  <FormInput
                    label="Chassis Code"
                    id="chassiscode"
                    name="chassiscode"
                    type="text"
                    value={formData.chassiscode || 'Not Selected'}
                    onChange={handleChange}
                    readOnly={true}
                    error={errors.chassiscode}
                  />
                  <div className="relative text-left">
                    <label htmlFor="vinNo" className="block text-sm font-medium text-[#333333] mb-1">
                      VIN
                      <span className="text-[#ff0000] ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      id="vinNo"
                      name="vinNo"
                      value={formData.vinNo}
                      onChange={handleChange}
                      placeholder="Enter VIN (max 17 characters)"
                      maxLength={17}
                      className={fieldClassName(errors.vinNo)}
                    />
                    {vinCheckLoading && (
                      <div className="absolute right-4 top-[42px] flex items-center">
                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                    {errors.vinNo && <p className="mt-1 text-sm text-[#ff0000]">{errors.vinNo}</p>}
                  </div>
                  <SelectInput
                    label="State"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    error={errors.state}
                  >
                    <option value="">Select State</option>
                    {states.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </SelectInput>
                  <ComboboxInput
                    label="AVV Approval Number"
                    value={formData.avvApprovalNumber}
                    onChange={handleChange}
                    name="avvApprovalNumber"
                    placeholder="Enter AVV Approval Number"
                    error={errors.avvApprovalNumber}
                    options={[
                      {
                        label: AVV_OPTIONS[0].approvalNumber,
                        value: AVV_OPTIONS[0].approvalNumber,
                        onSelect: () => {
                          setFormData(prev => ({
                            ...prev,
                            avvApprovalNumber: AVV_OPTIONS[0].approvalNumber
                          }));
                          setErrors(prev => ({
                            ...prev,
                            avvApprovalNumber: ""
                          }));
                        }
                      }
                    ]}
                  />
                  <ComboboxInput
                    label="AVV Email"
                    value={formData.avvEmail}
                    onChange={handleChange}
                    name="avvEmail"
                    type="email"
                    placeholder="Enter AVV Email"
                    error={errors.avvEmail}
                    options={[
                      {
                        label: AVV_OPTIONS[0].email,
                        value: AVV_OPTIONS[0].email,
                        onSelect: () => {
                          setFormData(prev => ({
                            ...prev,
                            avvEmail: AVV_OPTIONS[0].email
                          }));
                          setErrors(prev => ({
                            ...prev,
                            avvEmail: ""
                          }));
                        }
                      }
                    ]}
                  />
                </>
              )}
            </div>

            <div className="flex flex-col justify-center gap-3 pt-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={isSubmitting}
                className={`inline-flex items-center justify-center rounded-2xl border border-transparent bg-blue-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${isSubmitting ? 'cursor-not-allowed opacity-50' : ''
                  }`}
              >
                {isSubmitting ? 'Submitting...' : 'Add to Cart'}
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </button>
            </div>
          </form>
        </div>
      </div>
      {showSuccessModal && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
          <div className="w-full max-w-[25rem] overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)] sm:max-w-md">
            <div className="px-6 pt-6 text-center sm:px-8 sm:pt-8">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h3 className="mt-4 text-md font-medium uppercase tracking-[0.04em] text-slate-900">
                Thank you for submitting!
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Your model report request has been submitted successfully. You can proceed to checkout when ready.
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-2 border-t border-slate-200 bg-slate-50/70 px-6 py-4 sm:px-8">
              <button
                type="button"
                onClick={() => {
                  window.location.reload();
                }}
                className="inline-flex w-full items-center justify-center rounded-2xl border border-transparent bg-blue-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
              >
                Submit Another Form
              </button>

              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Submission Loader */}
      {isProcessingOrder && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
          <div className="w-full max-w-sm overflow-hidden rounded-[1rem] border border-slate-300 bg-white px-8 py-7 text-center ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)]">
            <div className="mx-auto flex h-12 w-12 items-center justify-center">
              <svg
                className="h-10 w-10 animate-spin text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <h3 className="mt-4 text-md font-medium uppercase tracking-[0.04em] text-slate-900">
              Processing Order
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Please wait while we place your order and prepare the invoice. This may take a few seconds.
            </p>
          </div>
        </div>,
        document.body
      )}

      {/* Cart Modal */}
      {showCartModal && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
          <div className="w-full max-w-[25rem] overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)] sm:max-w-md">
            <div className="px-6 pt-6 text-center sm:px-8 sm:pt-8">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                <svg className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.2 7.26a1 1 0 0 1-1.42.008L3.3 9.18a1 1 0 1 1 1.4-1.428l4.08 4.002 6.5-6.456a1 1 0 0 1 1.424-.008Z" clipRule="evenodd" />
                </svg>
              </div>

              <h3 className="mt-4 text-md font-medium uppercase tracking-[0.04em] text-slate-900">
                Order Placed!
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {cartInvoiceReady
                  ? 'Your order has been added to your cart. You can proceed to checkout when ready.'
                  : 'Your order is placed. The invoice is still being prepared — open the dashboard to track it, then pay when Pay Now appears.'}
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-2 border-t border-slate-200 bg-slate-50/70 px-6 py-4 sm:px-8">
              {cartInvoiceReady ? (
                <button
                  type="button"
                  onClick={() => navigate('/cart')}
                  className="inline-flex w-full items-center justify-center rounded-2xl border border-transparent bg-blue-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                >
                  Go to Cart
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex w-full items-center justify-center rounded-2xl border border-transparent bg-blue-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                >
                  Go to Dashboard
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  window.location.reload();
                }}
                className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Submit Another Form
              </button>

              {cartInvoiceReady ? (
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Back to Dashboard
                </button>
              ) : null}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Terms Modal */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccepted}
      />
    </CustomerLayout>
  );
};

export default MrAvvForm;
